import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useApi } from "./ApiContext";

export interface LocationInfo {
 locationId?: string; // Location ID for matching locationPricing
 country: string;
 countryCode: string;
 resolvedCountryCode?: string; // Actual location country code for business logic (when country override is active)
 currency: string;
 currencySymbol: string;
 taxRate: number;
 taxIncluded: boolean;
}

export interface StoreFallbackLocationInfo {
 country: string;
 countryCode: string;
}

interface CurrencyContextType {
 location: LocationInfo | null;
 storeFallbackLocation: StoreFallbackLocationInfo | null; // Store's resolved/fallback location (actual location being used)
 currency: string;
 loading: boolean;
 setLocation: (countryCode: string, currency?: string) => Promise<void>;
 availableCurrencies: string[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const { api } = useApi();
 const [location, setLocationState] = useState<LocationInfo | null>(null);
 const [storeFallbackLocation, setStoreFallbackLocation] = useState<StoreFallbackLocationInfo | null>(null);
 const [loading, setLoading] = useState(true);
 const [availableCurrencies] = useState<string[]>(["USD", "EUR", "GBP", "JPY", "CAD", "AUD"]);

 const fetchLocation = useCallback(async () => {
  try {
   setLoading(true);
   // Get location from backend - server manages currency/location detection
   const response = await api.get("/location");
   const locationData = response.data;
   
   const location: LocationInfo = {
    locationId: locationData.locationId,
    country: locationData.country || "Unknown",
    countryCode: locationData.countryCode || "US",
    resolvedCountryCode: locationData.resolvedCountryCode, // Actual location country code for business logic
    currency: locationData.currency || "USD",
    currencySymbol: locationData.currencySymbol || "$",
    taxRate: locationData.taxRate || 0,
    taxIncluded: locationData.taxIncluded || false,
   };
   
   setLocationState(location);
   
   // Store fallback location if provided and different from current location
   if (locationData.storeFallbackLocation) {
    setStoreFallbackLocation(locationData.storeFallbackLocation);
   } else {
    setStoreFallbackLocation(null);
   }
  } catch (err) {
   console.error("Error fetching location:", err);
   // Fallback to default if API fails
   const fallbackLocation: LocationInfo = {
    locationId: undefined,
    country: "United States",
    countryCode: "US",
    resolvedCountryCode: undefined,
    currency: "USD",
    currencySymbol: "$",
    taxRate: 0,
    taxIncluded: false,
   };
   setLocationState(fallbackLocation);
   setStoreFallbackLocation(null);
  } finally {
   setLoading(false);
  }
 }, [api]);

 useEffect(() => {
  fetchLocation();
 }, [fetchLocation]);

 const setLocation = useCallback(
  async (countryCode: string, currency?: string) => {
   try {
    // Update location via API query params - server will handle it
    const params = new URLSearchParams({ country: countryCode });
    if (currency) {
     params.append("currency", currency);
    }

    // Server will set location in session and return it
    const response = await api.get(`/location?${params.toString()}`);
    const locationData = response.data;
    
    const newLocation: LocationInfo = {
     locationId: locationData.locationId,
     country: locationData.country || countryCode,
     countryCode: locationData.countryCode || countryCode,
     resolvedCountryCode: locationData.resolvedCountryCode, // Actual location country code for business logic
     currency: locationData.currency || currency || "USD",
     currencySymbol: locationData.currencySymbol || "$",
     taxRate: locationData.taxRate || 0,
     taxIncluded: locationData.taxIncluded || false,
    };
    setLocationState(newLocation);
    if (locationData.storeFallbackLocation) {
     setStoreFallbackLocation(locationData.storeFallbackLocation);
    } else {
     setStoreFallbackLocation(null);
    }
   } catch (err) {
    console.error("Error setting location:", err);
   }
  },
  [api]
 );

 return (
  <CurrencyContext.Provider
   value={{
    location,
    storeFallbackLocation,
    currency: location?.currency || "USD",
    loading,
    setLocation,
    availableCurrencies,
   }}
  >
   {children}
  </CurrencyContext.Provider>
 );
};

export const useCurrency = (): CurrencyContextType => {
 const context = useContext(CurrencyContext);
 if (context === undefined) {
  throw new Error("useCurrency must be used within a CurrencyProvider");
 }
 return context;
};

